/**
 * Presential Payment API Service
 * Client for communicating with the Presential Payment endpoints in Payment Service
 * 
 * Requirements: 14.3, 14.4, 14.7, 14.8
 * 
 * This service handles the payment flow for presential courses:
 * - Get payment page data using token
 * - Create WebPay transaction
 * - Confirm payment after WebPay redirect
 */

/**
 * Payment Service base URL from environment variable
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * Default timeout for API requests (30 seconds)
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Creates an AbortController with timeout
 */
function createTimeoutController(timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Handles API response and extracts JSON data
 */
async function handleResponse(response) {
  const data = await response.json();
  
  if (!response.ok) {
    const errorMessage = data.error || data.errorMessage || 'Error en el servidor';
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

/**
 * Get payment page data for presential course
 * Requirements: 14.3, 14.4
 * 
 * @param {string} token - Payment token from email link
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Payment page data
 */
export async function getPaymentData(token, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/presential-payment/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status === 404) {
      const err = new Error('Enlace de pago no válido o no encontrado.');
      err.status = 404;
      throw err;
    }

    if (error.status === 410) {
      const err = new Error(error.message || 'Este enlace de pago ha expirado.');
      err.status = 410;
      err.expired = true;
      throw err;
    }

    if (error.status === 400 && error.data?.alreadyPaid) {
      const err = new Error('Este curso ya ha sido pagado.');
      err.status = 400;
      err.alreadyPaid = true;
      throw err;
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Create WebPay transaction for presential course
 * Requirements: 14.7, 14.8
 * 
 * @param {string} token - Payment token from email link
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - WebPay transaction data
 */
export async function createPresentialPayment(token, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/presential-payment/${encodeURIComponent(token)}/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status === 404) {
      throw new Error('Enlace de pago no válido o no encontrado.');
    }

    if (error.status === 410) {
      const err = new Error('Este enlace de pago ha expirado.');
      err.expired = true;
      throw err;
    }

    if (error.status === 400 && error.data?.alreadyPaid) {
      const err = new Error('Este curso ya ha sido pagado.');
      err.alreadyPaid = true;
      throw err;
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Confirm presential payment after WebPay redirect
 * Requirements: 14.8, 14.10
 * 
 * @param {Object} confirmData - Confirmation data from WebPay redirect
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Payment confirmation response
 */
export async function confirmPresentialPayment(confirmData, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/presential-payment/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(confirmData),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La confirmación ha excedido el tiempo de espera. Por favor verifica el estado de tu pago.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión al confirmar el pago. Por favor verifica el estado de tu pago.');
  }
}

/**
 * Redirects user to WebPay payment form
 * 
 * @param {string} webpayUrl - WebPay form URL
 * @param {string} webpayToken - WebPay token for form submission
 */
export function redirectToWebPay(webpayUrl, webpayToken) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = webpayUrl;
  form.style.display = 'none';

  const tokenInput = document.createElement('input');
  tokenInput.type = 'hidden';
  tokenInput.name = 'token_ws';
  tokenInput.value = webpayToken;
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
}

/**
 * Parses URL query parameters from WebPay redirect
 * 
 * @param {string} search - URL search string
 * @returns {Object} - Parsed parameters
 */
export function parseWebPayParams(search = window.location.search) {
  const params = new URLSearchParams(search);
  
  return {
    token_ws: params.get('token_ws') || undefined,
    TBK_TOKEN: params.get('TBK_TOKEN') || undefined,
    TBK_ORDEN_COMPRA: params.get('TBK_ORDEN_COMPRA') || undefined,
    TBK_ID_SESION: params.get('TBK_ID_SESION') || undefined,
  };
}

/**
 * Determines the payment result type from WebPay parameters
 * 
 * @param {Object} params - WebPay redirect parameters
 * @returns {string} - Result type: 'success', 'cancelled', 'timeout', or 'unknown'
 */
export function getPaymentResultType(params) {
  if (params.token_ws) {
    return 'success';
  }
  if (params.TBK_TOKEN) {
    return 'cancelled';
  }
  if (params.TBK_ORDEN_COMPRA && params.TBK_ID_SESION) {
    return 'timeout';
  }
  return 'unknown';
}

export default {
  getPaymentData,
  createPresentialPayment,
  confirmPresentialPayment,
  redirectToWebPay,
  parseWebPayParams,
  getPaymentResultType,
};
